import { generateClient } from 'aws-amplify/api';
import awsconfig from '../aws-exports';

// Direct GraphQL fetch for anonymous users to avoid Cognito Identity Pool
const directGraphQLFetch = async (query, variables) => {
  const response = await fetch(awsconfig.aws_appsync_graphqlEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': awsconfig.aws_appsync_apiKey
    },
    body: JSON.stringify({ query, variables })
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

// Create authenticated client
let userPoolClient = null;
const getUserPoolClient = async () => {
  if (!userPoolClient) {
    const { generateClient } = await import('aws-amplify/api');
    userPoolClient = generateClient();
  }
  return userPoolClient;
};

// Fetch HypeRating for a single movie/show
export const fetchHypeRating = async (contentId, contentType, isAuthenticated) => {
  try {
    const query = contentType === 'MOVIE' ? 
      `query GetMovie($id: ID!) {
        getMovie(id: $id) {
          id
          hypeRating
          totalInteractions
        }
      }` :
      `query GetShow($id: ID!) {
        getShow(id: $id) {
          id
          hypeRating
          totalInteractions
        }
      }`;

    let result;
    if (isAuthenticated) {
      const client = await getUserPoolClient();
      result = await client.graphql({
        query,
        variables: { id: contentId },
        authMode: 'userPool'
      });
    } else {
      result = await directGraphQLFetch(query, { id: contentId });
    }

    const data = contentType === 'MOVIE' ? result.data?.getMovie : result.data?.getShow;
    const hypeData = {
      hypeRating: data?.hypeRating || 0,
      totalInteractions: data?.totalInteractions || 0
    };
    

    
    return hypeData;
  } catch (error) {
    console.log(`Could not fetch HypeRating for ${contentType}:`, error);
    return { hypeRating: 0, totalInteractions: 0 };
  }
};

// Track interaction with debouncing to prevent multiple calls
const trackingCache = new Map();

export const trackInteraction = async (interactionData, isAuthenticated) => {
  // Create cache key to prevent duplicate tracking - handle undefined userID
  const userId = interactionData.userID || 'anonymous';
  const contentId = interactionData.movieID || interactionData.showID;
  const cacheKey = `${userId}_${contentId}_${interactionData.action}_${interactionData.sessionID}`;
  const now = Date.now();
  
  // Check if same interaction was tracked recently (within 5 seconds)
  if (trackingCache.has(cacheKey)) {
    const lastTracked = trackingCache.get(cacheKey);
    if (now - lastTracked < 5000) {

      return null;
    }
  }
  
  trackingCache.set(cacheKey, now);
  
  try {
    const query = `
      mutation TrackInteraction($input: TrackInteractionInput!) {
        trackInteraction(input: $input) {
          id
          userID
          movieID
          showID
          contentType
          action
          timestamp
        }
      }
    `;
    
    // Use API key for all users since trackInteraction is marked as public
    const result = await directGraphQLFetch(query, { input: interactionData });
    
    return result.data?.trackInteraction;
  } catch (error) {
    console.error('Failed to track interaction:', {
      error,
      errorMessage: error?.message,
      graphqlErrors: error?.errors,
      graphqlErrorMessages: error?.errors?.map(e => e.message),
      networkError: error?.networkError,
      isAuthenticated,
      interactionData
    });
    
    // Log individual GraphQL errors for debugging
    if (error?.errors?.length > 0) {
      error.errors.forEach((gqlError, index) => {
        console.error(`GraphQL Error ${index + 1}:`, {
          message: gqlError.message,
          locations: gqlError.locations,
          path: gqlError.path,
          extensions: gqlError.extensions
        });
      });
    }
    trackingCache.delete(cacheKey); // Remove from cache on error
    throw error;
  }
};

// Fetch HypeRating for multiple items
export const fetchMultipleHypeRatings = async (items, contentType, isAuthenticated) => {
  try {
    const hypePromises = items.map(async (item) => {
      try {
        const hypeData = await fetchHypeRating(item.id, contentType, isAuthenticated);
        return {
          ...item,
          hypeRating: hypeData.hypeRating,
          totalInteractions: hypeData.totalInteractions
        };
      } catch (error) {
        console.log(`Error fetching HypeRating for ${item.id}:`, error);
        return { ...item, hypeRating: 0, totalInteractions: 0 };
      }
    });
    
    return await Promise.all(hypePromises);
  } catch (error) {
    console.log(`Could not fetch HypeRatings for ${contentType}s:`, error);
    return items.map(item => ({ ...item, hypeRating: 0, totalInteractions: 0 }));
  }
};

// Carousel-specific interaction tracking
export const trackCarouselInteraction = async (movieId, action, metadata, isAuthenticated, userId = null) => {
  const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  if (!sessionStorage.getItem('sessionId')) {
    sessionStorage.setItem('sessionId', sessionId);
  }

  const interactionData = {
    movieID: movieId,
    contentType: 'MOVIE',
    action,
    sessionID: sessionId,
    deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
    platform: 'web',
    metadata: JSON.stringify({
      ...metadata,
      source: 'carousel',
      timestamp: Date.now()
    }),
    isAnonymous: !isAuthenticated
  };
  
  // Only add userID for authenticated users
  if (isAuthenticated && userId) {
    interactionData.userID = userId;
  }

  try {
    const result = await trackInteraction(interactionData, isAuthenticated);
    if (result) {

    } else {

    }
    return result;
  } catch (error) {
    console.error(`🎬 Carousel: Failed to track ${action}:`, error);
    return null;
  }
};

// Show carousel-specific interaction tracking
export const trackShowCarouselInteraction = async (showId, action, metadata, isAuthenticated, userId = null) => {
  const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  if (!sessionStorage.getItem('sessionId')) {
    sessionStorage.setItem('sessionId', sessionId);
  }

  const interactionData = {
    showID: showId,
    contentType: 'SHOW',
    action,
    sessionID: sessionId,
    deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
    platform: 'web',
    metadata: JSON.stringify({
      ...metadata,
      source: 'carousel',
      timestamp: Date.now()
    }),
    isAnonymous: !isAuthenticated
  };
  
  // Only add userID for authenticated users
  if (isAuthenticated && userId) {
    interactionData.userID = userId;
  }

  try {
    const result = await trackInteraction(interactionData, isAuthenticated);
    if (result) {

    } else {

    }
    return result;
  } catch (error) {
    console.error(`📺 Carousel: Failed to track ${action}:`, error);
    return null;
  }
};

// Fetch top HypeRated movie IDs from DynamoDB for recommendations
export const fetchTopHypeRatedMovieIds = async (excludeMovieId, limit = 20, isAuthenticated) => {
  try {
    const query = `
      query ListMovies($limit: Int) {
        listMovies(limit: $limit) {
          items {
            id
            hypeRating
          }
        }
      }
    `;

    let result;
    if (isAuthenticated) {
      const client = await getUserPoolClient();
      result = await client.graphql({
        query,
        variables: { 
          limit: limit + 10 // Get extra to account for filtering
        },
        authMode: 'userPool'
      });
    } else {
      result = await directGraphQLFetch(query, { 
        limit: limit + 10
      });
    }

    const movies = result.data?.listMovies?.items || [];
    
    // Filter out current movie and sort by hypeRating
    const filteredMovies = movies
      .filter(movie => movie.id !== excludeMovieId && movie.hypeRating > 0)
      .sort((a, b) => (b.hypeRating || 0) - (a.hypeRating || 0))
      .slice(0, limit);
    
    // Return array of movie IDs with their hypeRating for MongoDB lookup
    return filteredMovies.map(movie => ({
      id: movie.id,
      hypeRating: movie.hypeRating
    }));
    
  } catch (error) {
    console.error('Error fetching top HypeRated movie IDs:', error);
    return [];
  }
};

// Fetch top HypeRated show IDs from DynamoDB for recommendations
export const fetchTopHypeRatedShowIds = async (excludeShowId, limit = 20, isAuthenticated) => {
  try {
    const query = `
      query ListShows($limit: Int) {
        listShows(limit: $limit) {
          items {
            id
            hypeRating
          }
        }
      }
    `;

    let result;
    if (isAuthenticated) {
      const client = await getUserPoolClient();
      result = await client.graphql({
        query,
        variables: { 
          limit: limit + 10 // Get extra to account for filtering
        },
        authMode: 'userPool'
      });
    } else {
      result = await directGraphQLFetch(query, { 
        limit: limit + 10
      });
    }

    const shows = result.data?.listShows?.items || [];
    
    // Filter out current show and sort by hypeRating
    const filteredShows = shows
      .filter(show => show.id !== excludeShowId && show.hypeRating > 0)
      .sort((a, b) => (b.hypeRating || 0) - (a.hypeRating || 0))
      .slice(0, limit);
    
    // Return array of show IDs with their hypeRating for MongoDB lookup
    return filteredShows.map(show => ({
      id: show.id,
      hypeRating: show.hypeRating
    }));
    
  } catch (error) {
    console.error('Error fetching top HypeRated show IDs:', error);
    return [];
  }
};

// Recalculate HypeRating for content
export const recalculateHypeRating = async (contentId, contentType) => {
  try {
    const query = `
      mutation CalculateHypeRating($contentID: ID!, $contentType: String!) {
        calculateHypeRating(contentID: $contentID, contentType: $contentType) {
          contentID
          contentType
          hypeRating
          hypeScore
        }
      }
    `;
    
    const result = await directGraphQLFetch(query, {
      contentID: contentId,
      contentType: contentType
    });
    
    return result.data?.calculateHypeRating;
  } catch (error) {
    console.error('Failed to recalculate HypeRating:', error);
    throw error;
  }
};

// Check if API key is properly configured
export const checkApiKeySetup = () => {
  try {

    return true;
  } catch (error) {
    console.error('API Key setup issue:', error);
    return false;
  }
};

